import { type FC, memo, useCallback, useMemo, useState } from "react";
import { useAtomValue } from "jotai";
import { ButtonKind, ButtonShape, ButtonSize, ButtonVariant, Heading, IconButton } from "@stubhub/broadway-web";
import { MoreIcon } from "@stubhub/broadway-web/icons/functional";
import { AddFeature, RecordParticipationTypes, useFeatureTernary } from "@viagogo/feature";
import { Content, useFormattedContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { MobileBannerCardGlassBackground } from "src/website-apps/responsive/stubhub-performer/components/mobile-banner/MobileBannerCardGlassBackground";
import { MobileBannerOverflowDrawer } from "src/website-apps/responsive/stubhub-performer/components/mobile-banner/MobileBannerOverflowDrawer";
import { MobileCredibilityBadge } from "src/website-apps/responsive/stubhub-performer/components/mobile-credibility-badge/MobileCredibilityBadge";
import { MobileCredibilityBadgePlacement } from "src/website-apps/responsive/stubhub-performer/components/mobile-credibility-badge/MobileCredibilityBadgePlacement";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { scrollToPerformerCardAtom } from "src/website-apps/responsive/stubhub-performer/utils/scrollToPerformerCardAtom";
import { TIM_MCGRAW_EXCLUSIVE_CATEGORY_ID } from "src/website-apps/shared/components/hero-banner/ImmersiveHeroBanner/ArtistInfoColumn";
import { ImageSize } from "src/website-apps/shared/components/image/constants";
import { Image } from "src/website-apps/shared/components/image/Image";
import { GRID_FILTER_TYPES } from "src/website-apps/shared/components/immersive-performer/eventlist-filters/constants";
import { HERO_BANNER_CONTENT_ID } from "src/website-apps/shared/constants/header";
import { TOP_LEVEL_CATEGORY } from "src/website-apps/shared/constants/TopLevelCategory";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useIsCalendarView } from "src/website-apps/shared/hooks/useIsCalendarView";
import { usePartnerTheme } from "src/website-apps/shared/hooks/usePartnerTheme";
import { usePerformerInfoCardData } from "src/website-apps/shared/hooks/usePerformerInfoCardData";
import {
  buttonClassesMoreInfo,
  buttonsContainer,
  imageStyle,
  inlineTitleAndButtonsContainer,
  mlbPartnerBackgroundRecipe,
  mobileCredibilityBadgeInHero,
  mobileBannerCardWrapper,
  mobileBannerCardWrapperWhite,
  mlbPlayoffsSubtitle,
  titleContainer,
  tourSubtitleStyle,
} from "./styles";

/**
 * Mobile banner content including performer image, name, and action buttons.
 *
 * Used in mobile layout on mobile and tablet devices
 */
export const MobileBannerContent: FC = memo(() => {
  const storePageEvent = useStorePageEvent();
  const { categorySummary, seoCity, performerGridSurface, tourFilterMetadata } = usePerformerPageModelData();
  const [state] = useEventsGridContext();
  const isParking = useMemo(() => state.filters.gridFilterType === GRID_FILTER_TYPES.PARKING, [state]);
  const { categoryId, categoryName, portraitImageUrl } = categorySummary;
  // override for Tim McGraw primary event
  const categoryTitle = categoryId === TIM_MCGRAW_EXCLUSIVE_CATEGORY_ID ? categoryName : categorySummary.categoryTitle;
  const partnerTheme = usePartnerTheme();
  const partnerLogoUrl = partnerTheme.partnerLogoLightSvgUrl ?? partnerTheme.partnerLogoUrl;
  const partnerBackgroundColor = partnerTheme.colors?.backgroundBrandActive;
  const glassImageUrl = portraitImageUrl ?? categorySummary.imageUrl;
  const { scrollToElement: scrollToPerformer } = useAtomValue(scrollToPerformerCardAtom);

  const mlbLogoBackgroundStyles = useMemo(
    () => ({
      "--mobile-banner-partner-logo-background-color": partnerBackgroundColor,
      "--mobile-banner-partner-logo-background-height": `${String(ImageSize.MobileBannerCardImage.height)}px`,
      "--mobile-banner-partner-logo-background-width": `${String(ImageSize.MobileBannerCardImage.width)}px`,
    }),
    [partnerBackgroundColor],
  );

  const mlbPartnerBackgroundClasses = mlbPartnerBackgroundRecipe({
    partnerColor: partnerBackgroundColor ? "custom" : "default",
  });

  // {categoryName} parking
  const categoryNameWithParking = useFormattedContent("cid$#3789327#", [categoryName ?? "?"]);

  const parkingContentId = categorySummary.topLevelCategoryId === 2 ? "cid$#3835736#" : "cid$#3833898#";
  // {categoryName} in {cityName} parking
  const categoryNameWithLocationAndParking = useFormattedContent(parkingContentId, [
    categoryName ?? "?",
    seoCity?.cityName ?? "",
  ]);
  const canParticiSeoCity = useCallback(() => !!seoCity?.cityName, [seoCity]);
  const seoCityTern = useFeatureTernary(FeatureSetEnum.TMEventListSEOCityFilterKillswitch, canParticiSeoCity);

  const parkingTitleContent = useMemo(
    () =>
      seoCityTern({
        control: () => categoryNameWithParking,
        variant: () => categoryNameWithLocationAndParking,
      }),
    [seoCityTern, categoryNameWithParking, categoryNameWithLocationAndParking],
  );
  const isGaShoppingMode = performerGridSurface?.type === "ga-festival";
  const isMlbPlayoffs = performerGridSurface?.type === "mlb-playoffs";
  const performerTitle = isGaShoppingMode ? categoryName : isParking ? parkingTitleContent : categoryTitle;
  const isConcert = categorySummary.topLevelCategoryId === TOP_LEVEL_CATEGORY.CONCERT;
  const tourTitle = tourFilterMetadata ? tourFilterMetadata.tourName : undefined;

  const { hasAnyData: catHasPerformerCard } = usePerformerInfoCardData();

  const { isOnCalendarView } = useIsCalendarView();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const handleDrawerOpen = useCallback(() => {
    setIsDrawerOpen(true);
    storePageEvent(PE_TYPE.CLICK, PE_KEYS.PERFORMER_MOBILE_OVERFLOW_OPEN);
  }, [storePageEvent]);
  const handleDrawerClose = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const wrapperClassName = isOnCalendarView ? mobileBannerCardWrapperWhite : mobileBannerCardWrapper;

  return (
    <div className={wrapperClassName}>
      <MobileBannerCardGlassBackground imageUrl={glassImageUrl ?? ""}>
        {!!partnerLogoUrl && (
          <div className={mlbPartnerBackgroundClasses} style={mlbLogoBackgroundStyles}>
            <Image alt={categoryTitle} size={ImageSize.MobileBannerMlbLogoImage} src={partnerLogoUrl} />
          </div>
        )}
        {!!portraitImageUrl && !partnerLogoUrl && (
          <Image
            alt={categoryTitle}
            size={ImageSize.MobileBannerCardImage}
            src={portraitImageUrl}
            srcSet={categorySummary.portraitCardImageSrcSet ?? undefined}
            sizes={`${ImageSize.MobileBannerCardImage.width}px`}
            loading="eager"
            fetchPriority="high"
            className={imageStyle}
          />
        )}
        <div className={inlineTitleAndButtonsContainer}>
          <div className={titleContainer}>
            <Heading id={HERO_BANNER_CONTENT_ID} variant="header" as="h1">
              {performerTitle}
            </Heading>
            {isMlbPlayoffs && (
              <span className={mlbPlayoffsSubtitle}>
                <Content id="cid$#4401471#" /> {/* MLB Playoffs */}
              </span>
            )}
            <AddFeature
              id={FeatureSetEnum.TMPerformerTourNameSubtitle}
              recordType={RecordParticipationTypes.IN_VIEW}
              canParticipate={() => isConcert && !!tourTitle}
              feature={() => <span className={tourSubtitleStyle}>{tourTitle}</span>}
            />
            <MobileCredibilityBadge
              placement={MobileCredibilityBadgePlacement.HERO}
              className={mobileCredibilityBadgeInHero}
            />
          </div>
          <div className={buttonsContainer}>
            <IconButton
              aria-label="More"
              classes={buttonClassesMoreInfo}
              variant={ButtonVariant.Ghost}
              size={ButtonSize.Small}
              kind={ButtonKind.Neutral}
              shape={ButtonShape.Round}
              icon={MoreIcon}
              onClick={handleDrawerOpen}
            />
          </div>
        </div>
      </MobileBannerCardGlassBackground>
      <MobileBannerOverflowDrawer
        isOpen={isDrawerOpen}
        handleClose={handleDrawerClose}
        categoryId={categoryId}
        scrollToPerformer={scrollToPerformer}
        categoryName={categoryName ?? ""}
        categoryImageUrl={categorySummary.portalImageUrl ?? null}
        catHasPerformerCard={!!catHasPerformerCard}
      />
    </div>
  );
});

MobileBannerContent.displayName = "MobileBannerContent";
